// src/hooks/useCountUp.js
import { useState, useEffect, useRef } from 'react';

/**
 * useCountUp Hook
 * Animated counter that counts from 0 to a target number
 * Triggers animation when element becomes visible in viewport
 *
 * @param {number} end - Target number to count up to
 * @param {number} duration - Animation duration in milliseconds (default: 2000)
 * @param {string} easing - Easing function: 'linear', 'easeOut', or 'easeInOut' (default: 'easeOut')
 * @returns {{ count: number, ref: React.RefObject }} - Current count value and ref to attach to element
 */
const useCountUp = (end, duration = 2000, easing = 'easeOut') => {
    const [count, setCount] = useState(0);
    const [hasAnimated, setHasAnimated] = useState(false);
    const elementRef = useRef(null);
    const observerRef = useRef(null);
    const frameRef = useRef(null);
    const startTimeRef = useRef(null);

    // Easing functions
    const easingFunctions = {
        linear: (t) => t,
        easeOut: (t) => 1 - Math.pow(1 - t, 3),
        easeInOut: (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
    };

    // Get the appropriate easing function
    const easeFn = easingFunctions[easing] || easingFunctions.easeOut;

    // Animation function using requestAnimationFrame
    const animate = (timestamp) => {
        if (!startTimeRef.current) {
            startTimeRef.current = timestamp;
        }

        const elapsed = timestamp - startTimeRef.current;
        const progress = Math.min(elapsed / duration, 1);

        // Apply easing function
        const easedProgress = easeFn(progress);

        // Calculate current count value
        const currentCount = Math.floor(easedProgress * end);
        setCount(currentCount);

        // Continue animation if not complete
        if (progress < 1) {
            frameRef.current = requestAnimationFrame(animate);
        } else {
            // Ensure we hit the exact target number
            setCount(end);
            setHasAnimated(true);
        }
    };

    // Start the animation
    const startAnimation = () => {
        if (hasAnimated) return;

        startTimeRef.current = null;
        frameRef.current = requestAnimationFrame(animate);
    };

    useEffect(() => {
        // Create Intersection Observer
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    // Trigger animation when element is visible
                    if (entry.isIntersecting && !hasAnimated) {
                        startAnimation();
                        // Disconnect observer after first trigger (triggerOnce: true)
                        if (observerRef.current && elementRef.current) {
                            observerRef.current.unobserve(elementRef.current);
                        }
                    }
                });
            },
            {
                threshold: 0.5 // Element must be 50% visible
            }
        );

        // Observe the element
        if (elementRef.current) {
            observerRef.current.observe(elementRef.current);
        }

        // Cleanup
        return () => {
            if (frameRef.current) {
                cancelAnimationFrame(frameRef.current);
            }
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [hasAnimated]); // eslint-disable-line react-hooks/exhaustive-deps

    return { count, ref: elementRef };
};

export default useCountUp;
